<template>
  <div class="w-full flex h-full flex-col pt-2 items-center md:max-w-[600px]">
    <img
      src="../../../../../assets/img/admin/loyalty-program.svg"
      class="w-20 mb-2 "
      alt="מתנה">
    <span class="text-lg font-medium mb-4">מועדון הלקוחות פעיל</span>

    <div class="w-full flex gap-3">
      <div
        class="bg-white border border-gray-200 flex justify-center items-center py-3 rounded-lg w-max px-10 m-auto gap-1 h-full ">
        <div class="h-full flex flex-col justify-between mx-2 text-sm items-center gap-2">
          <span>לקוחות במועדון</span>
          <span class="text-lg font-medium">
          <span v-if="!stats"> 0 </span>
              <count-up v-else :endVal="stats.numberOfMembers"></count-up>
          </span>
        </div>
      </div>
    </div>

    <!-- The page column has no side padding of its own — TCard brings its own p-4 — so the
         button needs matching inset or it bleeds to both screen edges. -->
    <div class="w-full px-4">
      <Button
        label="הזמנת לקוח/ה למועדון"
        icon="pi pi-user-plus"
        class="mt-4 w-full !rounded-3xl"
        @click="openInviteDrawer" />
    </div>

    <TCard class="mt-4 !gap-2 flex flex-col pb-0">
      <span class="self-start font-medium">הטבות במועדון</span>
      <div v-for="perk of perks" :key="perk.type" class="w-full px-4 mb-4 py-3 rounded-lg border border-gray flex">
        <img
          v-if="perk.type === 'birthday'"
          src="../../../../../assets/img/admin/lp-birthday.svg"
          class="w-12 ml-4 "
          alt="מתנה">
        <img
          v-if="perk.type === 'discount'"
          src="../../../../../assets/img/admin/lp-free-appointment.svg"
          class="w-12 ml-4 "
          alt="מתנה">
        <img
          v-if="perk.type === 'points'"
          src="../../../../../assets/img/admin/lp-gather-point.svg"
          class="w-12 ml-4 "
          alt="מתנה">

        <div class="flex flex-grow flex-col gap-1 items-start">
              <span class="font-medium ">
                {{ perk.name }}
              </span>
          <span class="text-sm">
                {{ perk.description }}
              </span>
        </div>
      </div>
    </TCard>

    <TCard v-if="members.length" class="!gap-3 flex flex-col">
      <div class="w-full flex items-center justify-between gap-2">
        <span class="font-medium">חברי המועדון</span>
        <span class="text-xs text-surface-500">{{ visibleMembers.length }} מתוך {{ members.length }}</span>
      </div>

      <!-- Search — same control as the customers list -->
      <IconField class="w-full">
        <InputIcon class="pi pi-search" />
        <InputText v-model="searchValue" size="small" placeholder="חיפוש לפי שם" class="w-full" />
      </IconField>

      <!-- Sort — the scrollable chip row the customers list uses for its segments -->
      <div class="w-full flex gap-1.5 overflow-x-auto sort-chips -mx-1 px-1 pb-0.5">
        <button
          v-for="option of sortOptions"
          :key="option.value"
          type="button"
          class="flex-none rounded-full border text-[11.5px] px-3 py-[5px] whitespace-nowrap transition-colors"
          :class="sortBy === option.value
            ? 'bg-primary-100 text-primary-700 border-primary-300 font-medium'
            : 'bg-white text-surface-600 border-surface-200'"
          @click="setSort(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div
        v-for="member of visibleMembers"
        :key="member.id"
        class="w-full px-4 py-3 rounded-lg border border-gray flex items-center gap-3 cursor-pointer hover:bg-surface-50 transition-colors"
        @click="goToMember(member.id)"
      >
        <div class="flex flex-grow flex-col gap-1 items-start min-w-0">
          <span class="font-medium truncate max-w-full">{{ member.name }}</span>
          <div class="text-xs text-surface-500 flex items-center gap-3 flex-wrap">
            <span v-if="member.joinedAt" class="flex items-center gap-1">
              <i class="pi pi-calendar text-[11px]" /> הצטרפ/ה {{ formatDate(member.joinedAt) }}
            </span>
            <span v-if="member.birthday" class="flex items-center gap-1">
              <i class="pi pi-gift text-[11px]" /> {{ formatDate(member.birthday) }}
            </span>
          </div>
        </div>
        <i class="pi pi-chevron-left !text-xs text-surface-400 flex-none" />
      </div>

      <!-- No search results -->
      <div v-if="!visibleMembers.length" class="w-full text-center text-surface-500 py-7 flex flex-col">
        <i class="pi pi-search text-2xl mb-2 block" />
        <span class="text-[13px]">לא נמצאו חברי מועדון בחיפוש הזה</span>
      </div>
    </TCard>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {DateTime} from "luxon";
import {ILoyaltyProgramMember, LoyaltyProgramPromosTypes} from "@torim-app/types";
import {useAdminStore} from "@app/stores";
import {LoyaltyProgramApi} from "@app/api/admin/loyalty-program-api";
import {useNavigation} from "@app/composables/navigation";
import {AdminRouteNames} from "@app/router/route-names";
import CountUp from 'vue-countup-v3'
import { trackEvent } from '@app/composables/posthog'
import { useDrawer } from '@torim-app/shared-components';
import InviteToLoyaltyProgram from '@app/components/admin/modals/InviteToLoyaltyProgram.vue';
import InputText from 'primevue/inputtext';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';

const adminStore = useAdminStore();
const { goTo } = useNavigation();
const { openDrawer, closeDrawer } = useDrawer();

const stats = ref<{ numberOfMembers: number }>();
const members = ref<ILoyaltyProgramMember[]>([]);

const formatDate = (date: Date | string) =>
  DateTime.fromISO(new Date(date).toISOString()).setLocale('he').toFormat('dd/MM/yyyy');

type SortOption = 'recent' | 'name' | 'birthday';

const searchValue = ref('');
const sortBy = ref<SortOption>('recent');

const sortOptions: Array<{ value: SortOption; label: string }> = [
  { value: 'recent', label: 'הצטרפו לאחרונה' },
  { value: 'name', label: 'לפי שם' },
  { value: 'birthday', label: 'יום הולדת קרוב' },
];

const setSort = (value: SortOption) => {
  sortBy.value = value;
  trackEvent('loyalty_members_sorted', { sort: value });
};

/** Days until the member's next birthday, ignoring the year. */
const daysToBirthday = (birthday?: Date | string): number => {
  if (!birthday) return Number.POSITIVE_INFINITY;
  const date = DateTime.fromJSDate(new Date(birthday));
  if (!date.isValid) return Number.POSITIVE_INFINITY;

  const today = DateTime.local().startOf('day');
  let next = date.set({ year: today.year }).startOf('day');
  if (next < today) next = next.plus({ years: 1 });
  return next.diff(today, 'days').days;
};

// The whole club is already loaded, so search and sort stay on the client — no refetch,
// no debounce, and the list stays responsive as the owner types.
const visibleMembers = computed(() => {
  const query = searchValue.value.trim().toLowerCase();
  const list = query
    ? members.value.filter(member => member.name?.toLowerCase().includes(query))
    : [...members.value];

  return list.sort((a, b) => {
    if (sortBy.value === 'name') return (a.name ?? '').localeCompare(b.name ?? '', 'he');
    if (sortBy.value === 'birthday') return daysToBirthday(a.birthday) - daysToBirthday(b.birthday);
    // 'recent' — newest join first, which is the order the API already returns.
    return new Date(b.joinedAt ?? 0).getTime() - new Date(a.joinedAt ?? 0).getTime();
  });
});

const openInviteDrawer = () => {
  trackEvent('loyalty_program_invite_drawer_opened');
  openDrawer(InviteToLoyaltyProgram, {
    onClose: closeDrawer,
    // The customer only joins once they confirm on the link, so refresh the club
    // list rather than assuming the invite added them.
    onInvited: () => {
      getStatistics();
      getMembers();
    }
  }, {
    title: 'הזמנה למועדון הלקוחות',
    closable: true
  });
};

const goToMember = (id: string) => {
  trackEvent('loyalty_member_clicked');
  goTo(AdminRouteNames.ClientProfile, { params: { id } });
};

const perks = computed<{ name: string; description: string; type: LoyaltyProgramPromosTypes | 'birthday'; }[]>(() => {
  const response: { name: string; description: string; type: LoyaltyProgramPromosTypes | 'birthday'; }[] = [];
  const {loyaltyProgram} = adminStore.businessSiteData ?? {};
  if (loyaltyProgram?.promoType === LoyaltyProgramPromosTypes.Point)
    response.push({
      name: 'צבירת נקודות על כל תור',
      description: `על כל תור תצברו ${loyaltyProgram?.pointsOptions?.percentageToGather}%  מעלות הביקור בנקודות. כל נקודה שווה שקל אחד.`,
      type: LoyaltyProgramPromosTypes.Point
    })
  else if (loyaltyProgram?.promoType === LoyaltyProgramPromosTypes.Discount) {
    const title = loyaltyProgram?.discountOptions?.discountAmount === 100 ?
      `אחרי ${loyaltyProgram?.discountOptions?.numberOfAppointments} תורים — התור הבא בחינם` :
      `אחרי ${loyaltyProgram?.discountOptions?.numberOfAppointments} תורים — התור הבא ב-${loyaltyProgram?.discountOptions?.discountAmount}% הנחה`
    response.push({
      name: title,
      description: 'כל תור מקרב אותך צעד נוסף לקראת תור מתנה!',
      type: LoyaltyProgramPromosTypes.Discount
    })
  }

  if (loyaltyProgram?.birthday?.shouldCollect)
    response.push({
      name: 'הטבת יום הולדת',
      description: 'יום ההולדת שלך הוא היום השמח שלך, ואין כמו לקבל הטבה כיפית במיוחד עבורך.',
      type: 'birthday'
    })

  return response
});

const getStatistics = () => {
  LoyaltyProgramApi.getStats()
    .then(res => {
      stats.value = res;
      trackEvent('loyalty_program_overview_viewed', { members_count: res.numberOfMembers });
    })
    .catch((err) => {
      console.log(err)
    });
}

const getMembers = () => {
  LoyaltyProgramApi.getMembers()
    .then(res => {
      members.value = res;
    })
    .catch((err) => {
      console.log(err)
    });
}

getStatistics();
getMembers();

</script>

<style scoped lang="scss">
/* The chip row scrolls sideways on a narrow phone; the bar itself would only add noise. */
.sort-chips {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
